import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import Grid from '~/components/core/Atomic/Grid/Grid';
import CheckBanner from '~/components/core/CheckBanner';
import { useCms } from '~/components/hooks/useCms';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import useIsConfigurationFieldSupportedByCategories, {
  IncidentTypeOverrideProvider,
} from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import { FNOL_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import mixpanel from '../../../../CmsMain/mixpanel';
import { ErrorHelperTextFormik } from '../../../../core/Formik/ErrorHelperTextFormik';
import AdditionalInformationDialog from '../AdditionalInformationDialog';
import { getIsSupplementaryInformationSectionActive } from '../Fragments/SupplementaryInformationFragment';
import { isSectionSatisfied } from '../sectionCompletion';

import styles from './index.module.scss';

const AdditionalInformationSection = ({
  id,
  sectionValues,
  onCancel,
  onSubmit,
  title,
  showOnly,
  sectionConfig,
  AdditionalInformationFragment,
  AdditionalInformationFragmentInitialValues,
  AdditionalInformationFragmentValidationSchema,
}) => {
  const { setFieldValue, values } = useFormikContext();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const { userOrganization } = useCms();
  const [showDialog, setShowDialog] = useState(false);
  const supportedBySubtypeCategory = isFieldSupportedByCategories(sectionConfig);
  const isActive =
    id === 'supplementary_information'
      ? getIsSupplementaryInformationSectionActive(
          incidentConfiguration,
          isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS),
          isFieldSupportedByCategories
        )
      : sectionConfig?.active;

  // checked_<id> is the only record that the user opened and saved this section: it is what survives a
  // remount and what a resumed FNOL draft restores.
  const isAcknowledged = get(values, `checked_${id}`, false);
  // An acknowledgement stops counting once the values behind it stop satisfying the section's current
  // schema — a mandatory field cleared out, or one that a sub-type change has just brought into scope.
  // Recomputed on every render rather than memoised: this component re-renders on every keystroke anywhere
  // in the FNOL form, but the work here costs about half of what the parent already spends on the same
  // section regardless. Measured through the real builders (getCustomSectionValidationSchema plus
  // getCustomSectionInitialValues against isSectionSatisfied), an 8-field custom section, 20k iterations
  // after warm-up, five runs: 0.054-0.079ms here against the parent's 0.090-0.179ms, i.e. 44-66% of a cost
  // that is already unconditional because the parent consumes Formik context and rebuilds both every render.
  // That last part is also why a memo could not have keyed on the schema or the seed: neither is ever
  // identity-stable, which is what made its dependency list a set of proxies for them.
  const isSectionComplete =
    isAcknowledged &&
    isSectionSatisfied({
      fieldSchemas: AdditionalInformationFragmentValidationSchema,
      sectionValues,
      seedValues: AdditionalInformationFragmentInitialValues,
    });

  const isGateWaived = !supportedBySubtypeCategory || !sectionConfig?.mandatory || sectionConfig?.active === false;

  useEffect(() => {
    setFieldValue(`verified_${id}`, isGateWaived || isSectionComplete);
  }, [id, isGateWaived, isSectionComplete, setFieldValue]);

  if (!isActive) {
    return <></>;
  }

  return (
    <>
      <Grid item xs={12} className={styles.AdditionalInformationSection}>
        <CheckBanner
          withBold
          note={title}
          checked={isSectionComplete}
          onClick={() => setShowDialog(true)}
          disabled={!supportedBySubtypeCategory}
        />
        <div className={styles.errorHelper}>
          <ErrorHelperTextFormik id={`verified_${id}`} />
        </div>
      </Grid>

      {showDialog && (
        <IncidentTypeOverrideProvider
          value={{ incidentType: values?.incident_type, incidentSubType: values?.incident_sub_type }}
        >
          <AdditionalInformationDialog
            id={id}
            open={showDialog}
            title={title}
            initialValues={sectionValues}
            additionalFieldOmitFn={(field) => isFieldSupportedByCategories(field)}
            AdditionalInformationFragment={AdditionalInformationFragment}
            AdditionalInformationFragmentInitialValues={AdditionalInformationFragmentInitialValues}
            AdditionalInformationFragmentValidationSchema={AdditionalInformationFragmentValidationSchema}
            showOnly={showOnly}
            onCancel={async () => {
              onCancel && (await onCancel());
              setShowDialog(false);
            }}
            onSubmit={async (values) => {
              onSubmit && (await onSubmit(values));
              setFieldValue(`checked_${id}`, true);
              setShowDialog(false);
              mixpanel.track(FNOL_MIXPANEL_EVENTS.FNOL_SECTION_DETAILS_UPDATE_CLICKED, { update_fnol_section: title });
            }}
          />
        </IncidentTypeOverrideProvider>
      )}
    </>
  );
};

AdditionalInformationSection.propTypes = {
  id: PropTypes.string.isRequired,
  dialogAction: PropTypes.node,
  disableEditIdentity: PropTypes.bool,
  sectionValues: PropTypes.object,
  isInsured: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  showOnly: PropTypes.bool,
  title: PropTypes.string.isRequired,
  sectionConfig: PropTypes.object,
  AdditionalInformationFragment: PropTypes.func,
  AdditionalInformationFragmentInitialValues: PropTypes.object,
  AdditionalInformationFragmentValidationSchema: PropTypes.object,
};

export default AdditionalInformationSection;
